import { useState, useCallback } from 'react';
import { Send, Check, AlertCircle } from 'lucide-react';
import { apiFetch } from '../lib/apiFetch';

interface NewsletterSignupProps {
  /** 'inline' for footer, 'banner' for blog sidebar / end of articles */
  variant?: 'inline' | 'banner';
  /** Source tag for analytics (e.g. 'footer', 'blog') */
  source?: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function NewsletterSignup({ variant = 'inline', source = 'footer' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setStatus('loading');
    setMessage('');

    try {
      const res = await apiFetch(`${API_BASE}/api/subscribe`, {
        method: 'POST',
        body: JSON.stringify({ email: trimmed, source }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('success');
        setMessage(data.message || "Thanks! You're subscribed.");
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  }, [email, source]);

  // ── Banner variant (blog sidebar / end of articles) ──
  if (variant === 'banner') {
    if (status === 'success') {
      return (
        <div className="mt-12 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
            <Check className="h-6 w-6 text-primary" />
          </div>
          <h3 className="mt-4 text-xl font-semibold">Thanks for subscribing!</h3>
          <p className="mt-2 text-muted-foreground">
            You'll receive our latest articles and insights in your inbox.
          </p>
        </div>
      );
    }

    return (
      <div className="mt-12 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-8">
        <div className="text-center">
          <h3 className="text-xl font-semibold">Stay Updated</h3>
          <p className="mt-2 text-muted-foreground">
            Get the latest articles on software development, AI, and digital transformation delivered to your inbox.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
          <input
            type="email"
            required
            placeholder="Enter your email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
            className="flex-1 max-w-sm rounded-lg border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Email address"
            disabled={status === 'loading'}
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
        {status === 'error' && message && (
          <p className="mt-3 text-center text-sm text-red-500 flex items-center justify-center gap-1">
            <AlertCircle className="h-4 w-4" />
            {message}
          </p>
        )}
        <p className="mt-3 text-center text-xs text-muted-foreground">
          We respect your privacy. Unsubscribe anytime.
        </p>
      </div>
    );
  }

  // ── Inline variant (footer) ──
  if (status === 'success') {
    return (
      <p className="text-sm font-medium text-primary">
        ✓ {message || "Thanks for subscribing!"}
      </p>
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex gap-2 w-full md:w-auto">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
          placeholder="you@example.com"
          required
          className="flex-1 md:w-64 rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          aria-label="Email address"
          disabled={status === 'loading'}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {status === 'loading' ? '...' : 'Subscribe'}
        </button>
      </form>
      {status === 'error' && message && (
        <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {message}
        </p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        We respect your privacy. Unsubscribe anytime.
      </p>
    </div>
  );
}
